import { useEffect, useMemo, useState } from 'react';
import type { GenerateNativeMessageSenderOptions } from './contracts';
import DispatcherEvent from './DispatcherEvent';
import generateNativeMessageSender from './generateNativeMessageSender';
import safeParse from './utils/safeParse';

type OwnArgs = GenerateNativeMessageSenderOptions;

const internalHost = Boolean(process.env.IS_LOCAL) ? 'localhost' : 'carvana';

export function processUrl(url: string): {
  url: string;
  urlPayload: Record<string, any>;
} {
  const urlPayload: Record<string, any> = {};

  // URI decode cvnaid so Native's upstream API can base64 decode it
  const urlWithCvnaId = url.replace(
    /([?&]cvnaid=)([^&]+)(.*)/g,
    (_, cvnaIdQuery, cvnaIdRaw, otherQuery) => {
      const cvnaid = decodeURIComponent(cvnaIdRaw);

      urlPayload.cvnaid = cvnaid;

      return cvnaIdQuery + cvnaid + otherQuery;
    }
  );

  return {
    url: urlWithCvnaId,
    urlPayload
  };
}

/**
 * Capture anchor clicks and send them to the native app
 *
 * Link types: browser, download, self, sheet
 *
 * @example
 * <a href="https://www.google.com" data-native-link="browser">Google</a>
 *
 * @example
 * <a href="https://www.google.com" data-native-link="self" data-native-payload={JSON.stringify({ vehicleId })}>Google</a>
 *
 * @example
 * <a href="https://www.google.com" data-native-included="false">Google</a>
 */

export default function useAnchorClickMessageDispatcher({
  logError,
  source,
  verboseLogs
}: OwnArgs) {
  const sendMessageToNative = useMemo(
    () => generateNativeMessageSender({ logError, source, verboseLogs }),
    [logError, source, verboseLogs]
  );

  const [previousEvent, setPreviousEvent] = useState({
    url: '',
    time: {} as Date
  });

  useEffect(() => {
    const isNative = window.navigator.userAgent.includes(
      'CarvanaNativeWebView'
    );

    if (!isNative) return () => {};

    function onClick(ev: MouseEvent) {
      if (!(ev.target instanceof Element)) return;

      const target = ev.target.closest<HTMLAnchorElement>('a[href]');

      if (
        target == null ||
        target.attributes.getNamedItem('href')?.value?.startsWith('#') // i.e. href="#foo-bar"
      ) {
        return;
      }

      ev.preventDefault();

      const { url, urlPayload } = processUrl(target.href);
      const {
        nativeLink = 'browser',
        nativePayload,
        nativeIncluded = 'true'
      } = target.dataset || {};

      if (!safeParse(nativeIncluded)) return;

      // Prevent double-clicks sending multiple events
      if (
        url === previousEvent.url &&
        Date.now() - previousEvent.time.getTime() < 500
      ) {
        setPreviousEvent({
          url: url,
          time: new Date()
        });

        return;
      }

      setPreviousEvent({
        url: url,
        time: new Date()
      });

      // If user is navigating to an internal page, we want to make sure we leave fullscreen mode
      if (url.includes(internalHost)) {
        sendMessageToNative(DispatcherEvent.LeaveFullscreen, {});
      }

      sendMessageToNative(DispatcherEvent.Link, {
        url,
        linkType: nativeLink,
        ...urlPayload,
        ...(nativePayload ? safeParse(nativePayload) : undefined)
      });
    }

    document.addEventListener('click', onClick, false);

    return () => {
      document.removeEventListener('click', onClick);
    };
  }, [previousEvent, sendMessageToNative]);
}
